/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:35:57                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-21 14:47:22                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * AddressSelect: address select component
    * onPlaceSelect: handle place selection
    * usePlaceWidget: define google autocomplete options
    * useEffect: reset value while value is null
*/

/* Imports */
import { t } from "i18next";
import { usePlacesWidget } from "react-google-autocomplete";
import { useEffect } from "react";
import { MapPinIcon } from "@heroicons/react/24/solid";
import { IconButton, Spinner } from "@material-tailwind/react";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { parseGooglePlace } from "./parseGooglePlace";
import { formatAddressLabel } from "./formatAddressLabel";
/***/

/**
 * AddressSelect: address select component
 * Used to select an address entity (wo or cust)
 * @param label - field label
 * @param onChange - on value change event
 * @param className - additionnal className
 * @param customerAddressLoading - show a loader on the customer shortcut button
 * while the caller resolves a display label from coordinates
 * @param onCustomerAddressClick - optional custom handler for the customer
 * shortcut button. When omitted, the component falls back to onChange(customerAddress).
*/
export default function AddressSelect({
  value,
  label,
  zone,
  onChange,
  className,
  customerAddress,
  allowEditCustomerAddress = false,
  customerAddressLoading = false,
  onCustomerAddressClick
}: {
  value?: EasyTech.Address,
  label?: string,
  zone?: EasyTech.Zone,
  onChange?: (address: EasyTech.Address | undefined) => void,
  className?: string,
  customerAddress?: EasyTech.Address,
  allowEditCustomerAddress?: boolean,
  customerAddressLoading?: boolean,
  onCustomerAddressClick?: () => void
}) {

  const getDefaultValue = () => formatAddressLabel(value);

  /**
  * onPlaceSelect: handle place selection
  * format address in correct format
  * @param place - google map place object 
  */
  const onPlaceSelected = (place: any) => {
    const address = parseGooglePlace(place, ref.current?.value || "");
    if (address && onChange) onChange(address);
  };
  /***/

  /**
  * usePlaceWidget: define google autocomplete options 
  */
  const { ref } = usePlacesWidget<HTMLInputElement>({
    onPlaceSelected: onPlaceSelected,
    options: {
      // "geocode" covers full addresses, localities and postal codes.
      // "address" alone hides cities/postal codes from autocomplete suggestions.
      types: ["geocode"],
      fields: [
        "address_components",
        "formatted_address",
        "geometry",
        "place_id",
      ]
    }
  });
  /***/

  /**
  * useEffect: sync displayed value with selected address
  */
  useEffect(() => {
    if (!ref.current) return;
    ref.current.value = getDefaultValue();
  }, [value, ref]);
  /***/

  return (
    <div className="field">
      {label &&<label>{t(label)}</label>}

      <div className="flex items-center gap-2">
        <div className="relative flex items-center w-full">
          <div className="absolute pl-2">
            <MapPinIcon className="w-5 text-gray-400"/>
          </div>
          
          {(customerAddress && value && customerAddress.latitude === value.latitude && customerAddress.longitude === value.longitude && !allowEditCustomerAddress) ?
            <input  type="text"
              ref={ref}
              className={`!pl-8 ${className}`}
              placeholder={t("common:address")}
              value={getDefaultValue()}
              onChange={(c) => onChange && onChange(undefined)}
              defaultValue={getDefaultValue()}/>
          :
            <input  type="text"
              ref={ref}
              placeholder={t("common:address")}
              className={`!pl-8 ${className}`}
              defaultValue={getDefaultValue()}/>
          }

          
          {customerAddress &&
            <IconButton onClick={() => {
                    if (onCustomerAddressClick) {
                      onCustomerAddressClick();
                      return;
                    }
                    if (onChange) onChange(customerAddress);
                  }}
                    variant="text"
                    className={"icon-btn ml-2"}
                    size="sm"
                    disabled={customerAddressLoading}>
              {customerAddressLoading ?
                <Spinner className="w-5 h-5" />
              :
                <BuildingOffice2Icon className="w-6" />
              }
            </IconButton>
          }
        </div>

        {zone && 
          <p className="block antialiased font-sans text-base font-light leading-relaxed text-nowrap text-gray-400">
            {t("common:zone")+"-"+zone.type}
          </p>
        }
      </div>
      
      {value && value.latitude === 0 && value.longitude === 0 && (
        <p className="mt-2 text-sm text-amber-600">
          {t("common:addressNotGeolocalized")}
        </p>
      )}
    </div>
  );
}
/***/
